import type { FormField } from '@kfolks/types'
import { FormFieldType, RequestPriority } from '@kfolks/types'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import { StaticComboBoxField } from './StaticComboBoxField'
import { StaticMultiComboBoxField } from './StaticMultiComboBoxField'

export interface WidgetProps {
	id: string
	field: FormField
	value: unknown
	disabled?: boolean
	ariaRequired: boolean
	ariaInvalid: boolean
	ariaDescribedBy?: string
	onChange: (next: unknown) => void
	onBlur: () => void
}

/**
 * Dispatch a single field's value/state to the right typed widget below.
 * Each branch is intentionally small so the file stays scannable; new field
 * types add a `case` plus a tiny component.
 */
export function FieldWidget(p: WidgetProps) {
	switch (p.field.type) {
		case FormFieldType.TEXT:
			return <TextLikeWidget {...p} type="text" />
		case FormFieldType.EMAIL:
			return <TextLikeWidget {...p} type="email" />
		case FormFieldType.NUMBER:
			return <NumberWidget {...p} />
		case FormFieldType.DATE:
			return <DateWidget {...p} />
		case FormFieldType.TEXTAREA:
			return <TextareaWidget {...p} />
		case FormFieldType.SELECT:
			return <SelectWidget {...p} />
		case FormFieldType.MULTISELECT:
			return <MultiselectWidget {...p} />
		case FormFieldType.RADIO:
			return <RadioWidget {...p} />
		case FormFieldType.CHECKBOX:
			return <CheckboxWidget {...p} />
		case FormFieldType.PRIORITY:
			return <PriorityWidget {...p} />
		default:
			return null
	}
}

// ─── Per-type widgets ────────────────────────────────────────────────────────

function commonAttrs(p: WidgetProps) {
	return {
		id: p.id,
		disabled: p.disabled,
		'aria-required': p.ariaRequired,
		'aria-invalid': p.ariaInvalid,
		'aria-describedby': p.ariaDescribedBy,
		onBlur: p.onBlur,
	}
}

function TextLikeWidget(p: WidgetProps & { type: 'text' | 'email' }) {
	return (
		<Input
			{...commonAttrs(p)}
			type={p.type}
			placeholder={p.field.placeholder}
			value={(p.value as string | undefined) ?? ''}
			onChange={(e) => p.onChange(e.target.value === '' ? undefined : e.target.value)}
		/>
	)
}

function NumberWidget(p: WidgetProps) {
	return (
		<Input
			{...commonAttrs(p)}
			type="number"
			placeholder={p.field.placeholder}
			value={p.value === undefined || p.value === null ? '' : String(p.value)}
			onChange={(e) => p.onChange(e.target.value === '' ? undefined : Number(e.target.value))}
		/>
	)
}

function DateWidget(p: WidgetProps) {
	const input = (
		<Input
			{...commonAttrs(p)}
			type="date"
			value={(p.value as string | undefined) ?? ''}
			onChange={(e) => p.onChange(e.target.value === '' ? undefined : e.target.value)}
		/>
	)
	if (p.field.withQuickPicks !== true) return input
	return (
		<div className="space-y-2">
			{input}
			<DateQuickPicks onPick={(iso) => p.onChange(iso)} disabled={p.disabled} />
		</div>
	)
}

/**
 * Preset chips that set the parent date field to a relative offset from today.
 * Today / +1m / +3m / +1y. All in the browser's local time — matches the
 * `<input type="date">` format (ISO yyyy-mm-dd).
 */
function DateQuickPicks({ onPick, disabled }: { onPick: (iso: string) => void; disabled?: boolean }) {
	const today = () => toIsoDate(new Date())
	const addMonths = (months: number) => {
		const d = new Date()
		d.setMonth(d.getMonth() + months)
		return toIsoDate(d)
	}
	const addYears = (years: number) => {
		const d = new Date()
		d.setFullYear(d.getFullYear() + years)
		return toIsoDate(d)
	}
	const presets: Array<{ label: string; value: () => string }> = [
		{ label: 'Today', value: today },
		{ label: '+1 month', value: () => addMonths(1) },
		{ label: '+3 months', value: () => addMonths(3) },
		{ label: '+1 year', value: () => addYears(1) },
	]
	return (
		<div className="flex flex-wrap gap-2">
			{presets.map((p) => (
				<button
					key={p.label}
					type="button"
					disabled={disabled}
					onClick={() => onPick(p.value())}
					className="rounded-md border border-input bg-background px-2.5 py-1 text-xs text-muted-foreground hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
				>
					{p.label}
				</button>
			))}
		</div>
	)
}

function toIsoDate(d: Date): string {
	const y = d.getFullYear()
	const m = String(d.getMonth() + 1).padStart(2, '0')
	const day = String(d.getDate()).padStart(2, '0')
	return `${y}-${m}-${day}`
}

function TextareaWidget(p: WidgetProps) {
	return (
		<textarea
			{...commonAttrs(p)}
			placeholder={p.field.placeholder}
			value={(p.value as string | undefined) ?? ''}
			onChange={(e) => p.onChange(e.target.value === '' ? undefined : e.target.value)}
			className={cn(
				'flex min-h-[5rem] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background',
				'placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
				'disabled:cursor-not-allowed disabled:opacity-50',
			)}
		/>
	)
}

function SelectWidget(p: WidgetProps) {
	const options = p.field.options ?? []
	return (
		<StaticComboBoxField
			id={p.id}
			ariaLabel={p.field.label}
			ariaInvalid={p.ariaInvalid}
			ariaDescribedBy={p.ariaDescribedBy}
			disabled={p.disabled}
			placeholder={p.field.placeholder}
			options={options}
			value={p.value as string | undefined}
			onChange={(next) => p.onChange(next)}
			onBlur={p.onBlur}
		/>
	)
}

function MultiselectWidget(p: WidgetProps) {
	const options = p.field.options ?? []
	const arr = Array.isArray(p.value) ? (p.value as string[]) : []
	return (
		<StaticMultiComboBoxField
			id={p.id}
			ariaLabel={p.field.label}
			ariaInvalid={p.ariaInvalid}
			ariaDescribedBy={p.ariaDescribedBy}
			disabled={p.disabled}
			placeholder={p.field.placeholder}
			options={options}
			value={arr}
			onChange={(next) => p.onChange(next)}
			onBlur={p.onBlur}
		/>
	)
}

function RadioWidget(p: WidgetProps) {
	const options = p.field.options ?? []
	return (
		<div
			role="radiogroup"
			aria-required={p.ariaRequired}
			aria-invalid={p.ariaInvalid}
			aria-describedby={p.ariaDescribedBy}
		>
			{options.map((o) => (
				<label key={o.value} className="flex items-center gap-2 py-1 text-sm">
					<input
						type="radio"
						name={p.id}
						value={o.value}
						checked={p.value === o.value}
						disabled={p.disabled}
						onChange={() => p.onChange(o.value)}
						onBlur={p.onBlur}
						className="h-4 w-4 border-input text-primary focus:ring-ring"
					/>
					{o.label}
				</label>
			))}
		</div>
	)
}

function CheckboxWidget(p: WidgetProps) {
	return (
		<input
			{...commonAttrs(p)}
			type="checkbox"
			checked={p.value === true}
			onChange={(e) => p.onChange(e.target.checked)}
			className="h-4 w-4 rounded border-input text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
		/>
	)
}

function PriorityWidget(p: WidgetProps) {
	const options = Object.values(RequestPriority).map((priority) => ({
		label: capitalize(priority),
		value: priority,
	}))
	return (
		<StaticComboBoxField
			id={p.id}
			ariaLabel={p.field.label}
			ariaInvalid={p.ariaInvalid}
			ariaDescribedBy={p.ariaDescribedBy}
			disabled={p.disabled}
			placeholder={p.field.placeholder ?? 'Select priority…'}
			options={options}
			value={p.value as string | undefined}
			onChange={(next) => p.onChange(next)}
			onBlur={p.onBlur}
		/>
	)
}

function capitalize(s: string): string {
	return s.length === 0 ? s : s[0].toUpperCase() + s.slice(1)
}
